import React, { useEffect, useRef, useState } from 'react';
import { bool, func, string } from 'prop-types';

import { Button, Modal } from '../index';
import { sendContactRequest } from '../../util/api';

import css from './SellerInquiryModal.module.css';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const SellerInquiryModal = props => {
  const { isOpen, onClose, onManageDisableScrolling, title } = props;
  const [successMessage, setSuccessMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const turnstileRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!window.turnstile) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => setTurnstileLoaded(true);
      document.head.appendChild(script);
    } else {
      setTurnstileLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSuccessMessage('');
      setSending(false);
    }
  }, [isOpen]);

  const handleOverlayClick = e => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSending(true);
    setSuccessMessage('');

    const form = e.target;
    const name = form.name.value;
    const email = form.email.value;
    const message = form.message.value;
    const website = form.website.value;
    const turnstileToken = form['cf-turnstile-response']?.value;

    if (website) {
      setSuccessMessage('Your message has been sent!');
      form.reset();
      setSending(false);
      setTimeout(() => onClose(), 2000);
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setSuccessMessage('Please complete the security check.');
      setSending(false);
      return;
    }

    try {
      await sendContactRequest({
        name,
        email,
        message,
        website,
        'cf-turnstile-response': turnstileToken,
      });

      setSuccessMessage('Your message has been sent!');
      form.reset();
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.reset(turnstileRef.current);
      }
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      console.error('Seller inquiry form error:', err);
      const errorMsg =
        err?.error || err?.errors?.map(error => error.message).join(', ') ||
        'There was an error sending your message. Please try again.';
      setSuccessMessage(errorMsg);
    }

    setSending(false);
  };

  return (
    <Modal
      id="SellerInquiryModal"
      containerClassName={css.modalContainer}
      isOpen={isOpen}
      onClose={onClose}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <div className={css.overlayClickTarget} onClick={handleOverlayClick}>
        <div className={css.modalContent}>
          <h2 className={css.modalTitle}>{title}</h2>
          <form className={css.contactForm} onSubmit={handleSubmit}>
            <div className={css.formField}>
              <label htmlFor="sellerInquiryName">Name</label>
              <input type="text" id="sellerInquiryName" name="name" required />
            </div>
            <div className={css.formField}>
              <label htmlFor="sellerInquiryEmail">Email</label>
              <input type="email" id="sellerInquiryEmail" name="email" required />
            </div>
            <div className={css.formField}>
              <label htmlFor="sellerInquiryMessage">Message</label>
              <textarea id="sellerInquiryMessage" name="message" rows="4" required />
            </div>

            <div className={css.honeypotField} aria-hidden="true">
              <label htmlFor="sellerInquiryWebsite">Website (leave blank)</label>
              <input
                type="text"
                id="sellerInquiryWebsite"
                name="website"
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            {TURNSTILE_SITE_KEY ? (
              <div
                ref={turnstileRef}
                className="cf-turnstile"
                data-sitekey={TURNSTILE_SITE_KEY}
                data-theme="light"
                data-size="normal"
              />
            ) : null}

            <Button type="submit" className={css.submitButton} disabled={sending || !turnstileLoaded}>
              {sending ? 'Sending...' : 'Send Message'}
            </Button>
          </form>

          {successMessage ? <div className={css.successMessage}>{successMessage}</div> : null}
        </div>
      </div>
    </Modal>
  );
};

SellerInquiryModal.defaultProps = {
  title: 'Request Information',
  onManageDisableScrolling: () => null,
};

SellerInquiryModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onManageDisableScrolling: func,
  title: string,
};

export default SellerInquiryModal;
